import { CORE_HOURS_COLUMNS } from './columnSpec.core';
import { PLB_HOURS_COLUMNS } from './columnSpec.plb';
import { matchesClaFlags, plb, text } from './columnSpec.types';

import type {
  HoursColumnSpec,
  HoursGroupSpec,
  HoursSectionSpec
} from './columnSpec.types';

/**
 * Compound header groups in display order, mirroring the order the Dojo `columns` array was built in
 * (`EmployeeWorkAgreementsGridColumns.js:156-577` then `:686-905`). A group with no visible columns
 * for the current CLA flags is dropped automatically by the builder.
 */
export const HOURS_GROUPS: HoursGroupSpec[] = [
  { id: 'period', label: text('groups.period') },
  { id: 'contract', label: text('groups.contract') },
  { id: 'contractDetails', label: text('groups.contractDetails') },
  { id: 'delivering', label: text('groups.delivering') },
  { id: 'deliveringDetails', label: text('groups.deliveringDetails') },
  { id: 'hoursBalance', label: text('groups.hoursBalance') },
  { id: 'hoursBalanceDetails', label: text('groups.hoursBalanceDetails') },
  { id: 'leave', label: text('groups.leave') },
  { id: 'leaveStructure', label: text('groups.leaveStructure') },
  { id: 'leaveCorrection', label: text('groups.leaveCorrection') },
  { id: 'leaveRecord', label: text('groups.leaveRecord') },
  { id: 'leaveStatutory', label: text('groups.leaveStatutory') },
  { id: 'leaveCompensation', label: text('groups.leaveCompensation') },
  { id: 'leaveExtralegal', label: text('groups.leaveExtralegal') },
  { id: 'plb', label: plb('groups.plb') },
  { id: 'plbDetails', label: plb('groups.plbDetails') },
  { id: 'plbDistribution', label: plb('groups.plbDistribution') },
  { id: 'plbRecording', label: plb('groups.plbRecording') },
  { id: 'plbSummary', label: plb('groups.plbSummary') },
  { id: 'plbHistory', label: plb('groups.plbHistory') },
  { id: 'llStructure', label: text('groups.llStructure') },
  { id: 'llDeposit', label: text('groups.llDeposit') },
  { id: 'llDetails', label: text('groups.llDetails') },
  { id: 'balanceBudget', label: plb('groups.balanceBudget') }
];

/**
 * The header row above the groups. `period` sits in no section — it is the row identity and stays
 * pinned to the left. A section whose groups are all gated away (PLB without PLB_VISIBILITY)
 * disappears with them.
 */
export const HOURS_SECTIONS: HoursSectionSpec[] = [
  {
    id: 'contractHours',
    label: text('sections.contractHours'),
    groups: [
      'contract',
      'contractDetails',
      'delivering',
      'deliveringDetails',
      'hoursBalance',
      'hoursBalanceDetails'
    ]
  },
  {
    id: 'leave',
    label: text('sections.leave'),
    groups: [
      'leave',
      'leaveStructure',
      'leaveCorrection',
      'leaveRecord',
      'leaveStatutory',
      'leaveCompensation',
      'leaveExtralegal'
    ]
  },
  {
    id: 'plbAndLl',
    // the tenant names the budget, so the section title carries plbName too
    label: plb('sections.plbAndLl'),
    groups: [
      'plb',
      'plbDetails',
      'plbDistribution',
      'plbRecording',
      'plbSummary',
      'plbHistory',
      'llStructure',
      'llDeposit',
      'llDetails',
      'balanceBudget'
    ]
  }
];

export const HOURS_COLUMN_SPECS: HoursColumnSpec[] = [
  ...CORE_HOURS_COLUMNS,
  ...PLB_HOURS_COLUMNS
];

/**
 * Every value of one month, for the detail window: the whole column spec plus its detail-only rows,
 * minus the period (which titles the window) and the rows the grid keeps to itself.
 *
 * The window is the complete picture of a month; the grid stays the year overview. Showing only the
 * grid's hidden columns left groups with half their rows and no hint the rest existed.
 */
export const detailColumnSpecs = (
  flags: string[] = [],
  showBalanceBudget = false
): HoursColumnSpec[] =>
  HOURS_COLUMN_SPECS.filter(
    (spec) =>
      !spec.gridOnly &&
      spec.render !== 'period' &&
      (!spec.requiresBalanceBudget || showBalanceBudget) &&
      matchesClaFlags(spec, flags)
  );

/** The grid's own columns: everything the spec does not mark as detail-window-only. */
export const gridColumnSpecs = (): HoursColumnSpec[] =>
  HOURS_COLUMN_SPECS.filter((spec) => !spec.detailOnly);
